/**
 * "Is this being saved?" answered honestly, in one line.
 *
 * Presentation only. It reads the persistence layer's status feed and never
 * writes, scores or blocks anything. Four states a learner can act on:
 *   queued   — on this device, on its way
 *   saving   — writing now
 *   saved    — in your account
 *   retrying — connection trouble, nothing lost
 */
import { useEffect, useState } from "react";
import { Check, CloudOff, Loader2, RefreshCw } from "lucide-react";
import {
  getSaveStatus,
  subscribeSaveStatus,
  type SaveStatus as Status,
} from "../persistence";

function label(s: Status): { text: string; tone: string; icon: typeof Check; spin?: boolean } {
  if (s.completed) return { text: "Career profile saved", tone: "text-primary", icon: Check };
  switch (s.state) {
    case "saving":
      return { text: "Building your profile…", tone: "text-muted-foreground", icon: Loader2, spin: true };
    case "saved":
      return { text: "Saved to your profile", tone: "text-primary", icon: Check };
    case "queued":
      return { text: "Building your profile…", tone: "text-muted-foreground", icon: Loader2, spin: true };
    case "retrying":
      return { text: "Safe on this device", tone: "text-celebrate", icon: CloudOff };
    default:
      return { text: "Your journey starts here", tone: "text-muted-foreground", icon: RefreshCw };
  }

}

export function SaveStatus({ className = "" }: { className?: string }) {
  const [status, setStatus] = useState<Status>(getSaveStatus);
  useEffect(() => subscribeSaveStatus(setStatus), []);

  if (status.state === "idle" && !status.completed) return null;

  const { text, tone, icon: Icon, spin } = label(status);

  return (
    <p
      data-testid="v2-save-status"
      data-state={status.completed ? "completed" : status.state}
      role="status"
      aria-live="polite"
      title={text}
      className={`flex items-center gap-1.5 text-[11px] font-medium ${tone} ${className}`}
    >
      <Icon className={`h-3.5 w-3.5 ${spin ? "animate-spin" : ""}`} aria-hidden="true" />
      <span className="hidden sm:inline">{text}</span>
      <span className="sr-only sm:hidden">{text}</span>
      {status.pending > 0 && (
        <span className="sr-only">{status.pending} answers still to sync</span>
      )}
    </p>
  );
}

export default SaveStatus;
